// Capability flags for a data-source KIND, read from the node catalog.
//
// These used to be hardcoded Sets duplicated across the frontend (StorageDetail's
// UNSTRUCTURED / ENGINE_READABLE / DB_PROFILABLE / AGENTIC, DataSourcesTable's
// AGENTIC_KINDS). They now come from the `source_kind` registry via
// GET /api/node_catalog, so adding a kind no longer means editing this layer.
//
// The old Sets survive as FALLBACK only: the catalog is fetched asynchronously,
// and a component must render correctly on its first paint and for a legacy kind
// the registry doesn't list (e.g. a stored kind of 'csv'). getKindFlags is
// synchronous-friendly for that reason — it returns the fallback until the
// catalog resolves, and never throws.

import { getNodeCatalog } from './pipeline';

// Fallback sets — transcribed from the values they replace. Used before the
// catalog resolves and for kinds absent from it.
const FALLBACK_UNSTRUCTURED = new Set(['slack', 'gmail']);
const FALLBACK_ENGINE_READABLE = new Set([
  'postgres', 'redshift', 'mysql', 'sqlserver', 'oracle', 'snowflake', 'bigquery', 'mongo', 'file',
  'api', 'slack', 'gmail', 'scraper', 'enrichment', 'rss', 'rag',
]);
const FALLBACK_AGENTIC = new Set(['api', 'scraper', 'enrichment', 'rss', 'rag']);
const FALLBACK_DB_PROFILABLE = new Set([
  'postgres', 'redshift', 'mysql', 'sqlserver', 'oracle', 'snowflake', 'bigquery', 'mongo',
]);

const fallbackFlags = (k) => ({
  is_structured: !FALLBACK_UNSTRUCTURED.has(k),
  engine_readable: FALLBACK_ENGINE_READABLE.has(k),
  db_profilable: FALLBACK_DB_PROFILABLE.has(k),
  is_agentic: FALLBACK_AGENTIC.has(k),
});

// kind -> flags, built once per session from the catalog. Null until loaded.
let flagsByKind = null;
let inFlight = null;

function indexConnectors(connectors) {
  const map = new Map();
  for (const c of connectors || []) {
    // Load connectors win: every flag here describes reading a SOURCE, and
    // postgres/mongo appear twice (once as load, once as save).
    const kind = c.storage_type_out || c.storage_type_in;
    if (!kind) continue;
    if (map.has(kind) && c.role !== 'load') continue;
    map.set(String(kind).toLowerCase(), {
      is_structured: c.is_structured !== false,
      engine_readable: !!c.engine_readable,
      db_profilable: !!c.db_profilable,
      is_agentic: c.family === 'agentic',
    });
  }
  return map;
}

// Warm the cache. Safe to call repeatedly and concurrently; resolves to the map.
export function loadSourceKindFlags() {
  if (flagsByKind) return Promise.resolve(flagsByKind);
  if (!inFlight) {
    inFlight = getNodeCatalog()
      .then((cat) => { flagsByKind = indexConnectors(cat?.connectors); return flagsByKind; })
      .catch(() => { inFlight = null; return null; }); // stay on the fallback; retry next call
  }
  return inFlight;
}

// Flags for one kind. Returns the registry's values once the catalog has loaded,
// the hardcoded fallback before that or for an unlisted kind.
export function getKindFlags(kind) {
  const k = String(kind || '').toLowerCase();
  const fromCatalog = flagsByKind && flagsByKind.get(k);
  return fromCatalog || fallbackFlags(k);
}
